import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppShell from './AppShell';
import WeChatOAuthQrDisplay from './WeChatOAuthQrDisplay';
import { API_BASE_URL } from '../config/api';
import { isWeChatEmbeddedBrowser } from '../utils/wechat';
import { getSignInReturnTo } from '../utils/signInReturn';
import { wechatLoginLog } from '../utils/wechatLoginLog';
import { useWeChatQrPollSession } from '../hooks/useWeChatQrPollSession';
import { ChevronLeft } from 'lucide-react';
import './Login.css';
import './LoginMethod.css';

const LoginMethod: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [wechatBusy, setWechatBusy] = useState(false);
  const [wechatErr, setWechatErr] = useState<string | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrAuthUrl, setQrAuthUrl] = useState<string | null>(null);
  const [qrPollingKey, setQrPollingKey] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrFetchErr, setQrFetchErr] = useState<string | null>(null);

  useWeChatQrPollSession(showQrModal && qrPollingKey ? qrPollingKey : null, {
    onExpired: () => {
      setQrFetchErr((prev) => prev || '等待超时，请关闭后重新打开微信扫码');
    },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/auth/wechat/config`);
        const j = await r.json();
        if (!cancelled) {
          const ok = Boolean(j.success && j.data?.configured);
          setConfigured(ok);
          wechatLoginLog('config_loaded', {
            httpOk: r.ok,
            httpStatus: r.status,
            configured: ok,
            hasAppId: Boolean(j.data?.hasAppId),
          });
        }
      } catch (e) {
        if (!cancelled) {
          setConfigured(false);
          wechatLoginLog('config_fetch_failed', { err: e instanceof Error ? e.message : String(e) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onAccount = () => {
    navigate('/login', { state: location.state });
  };

  const onWeChat = async () => {
    if (!configured) {
      wechatLoginLog('wechat_click_blocked', { reason: 'not_configured' });
      window.alert(
        '微信登录尚未配置。请在服务器配置 WECHAT_OAUTH_APP_ID、WECHAT_OAUTH_APP_SECRET、WECHAT_OAUTH_REDIRECT_URI（或旧名 WECHAT_OPEN_*）；生产环境还需配置 WECHAT_OAUTH_SUCCESS_REDIRECT（微信回调成功后跳转的前端地址）。',
      );
      return;
    }
    if (isWeChatEmbeddedBrowser()) {
      wechatLoginLog('inapp_start_request', { apiBase: API_BASE_URL });
      try {
        const r = await fetch(`${API_BASE_URL}/auth/wechat/start?mode=inapp`);
        const j = await r.json();
        wechatLoginLog('inapp_start_response', {
          httpOk: r.ok,
          httpStatus: r.status,
          success: Boolean(j.success),
          hasUrl: Boolean(j.data?.url),
          mode: j.data?.mode,
          error: j.success ? undefined : (j as { error?: string }).error,
        });
        if (j.success && j.data?.url) {
          wechatLoginLog('inapp_redirect_oauth', { authUrlLen: (j.data.url as string).length });
          window.location.href = j.data.url as string;
          return;
        }
        window.alert((j as { error?: string }).error || '无法发起微信授权');
      } catch (e) {
        wechatLoginLog('inapp_start_exception', { err: e instanceof Error ? e.message : String(e) });
        window.alert('网络错误，请稍后重试');
      }
      return;
    }
    setWechatErr(null);
    wechatLoginLog('pc_qr_modal_open');
    setShowQrModal(true);
    setQrFetchErr(null);
    setQrAuthUrl(null);
    setQrPollingKey(null);
    setQrLoading(true);
    setWechatBusy(true);
    try {
      wechatLoginLog('pc_qr_start_request', { apiBase: API_BASE_URL });
      const r = await fetch(`${API_BASE_URL}/auth/wechat/start?mode=qr`);
      const j = await r.json();
      wechatLoginLog('pc_qr_start_response', {
        httpOk: r.ok,
        httpStatus: r.status,
        success: Boolean(j.success),
        hasUrl: Boolean(j.data?.url),
        mode: j.data?.mode,
        error: j.success ? undefined : (j as { error?: string }).error,
      });
      if (j.success && j.data?.url) {
        setQrAuthUrl(j.data.url as string);
        const pk = typeof (j.data as { pollingKey?: string }).pollingKey === 'string' ? (j.data as { pollingKey: string }).pollingKey : null;
        setQrPollingKey(pk);
        wechatLoginLog('pc_qr_auth_url_ready', {
          authUrlLen: (j.data.url as string).length,
          hasPollingKey: Boolean(pk),
        });
      } else {
        setQrFetchErr((j as { error?: string }).error || '无法加载微信登录，请稍后重试');
      }
    } catch (e) {
      wechatLoginLog('pc_qr_start_exception', { err: e instanceof Error ? e.message : String(e) });
      setQrFetchErr('网络错误，请稍后重试');
    } finally {
      setQrLoading(false);
      setWechatBusy(false);
    }
  };

  const closeQrModal = () => {
    wechatLoginLog('pc_qr_modal_close');
    setShowQrModal(false);
    setQrAuthUrl(null);
    setQrPollingKey(null);
    setQrFetchErr(null);
    setQrLoading(false);
  };

  const goBack = useCallback(() => {
    navigate(getSignInReturnTo(location.state), { replace: true });
  }, [navigate, location.state]);

  return (
    <AppShell centerBody>
      <div className="login-box">
        <div className="login-flow-back-row">
          <button type="button" className="login-method-back login-method-back--top" onClick={goBack}>
            <ChevronLeft size={18} strokeWidth={2.25} aria-hidden />
            <span>返回</span>
          </button>
        </div>
        <div className="login-title-brand">luludomi</div>
        <h2>选择登录方式</h2>
        <div className="login-method-actions">
          <button type="button" className="submit-button" onClick={onAccount}>
            账号登录
          </button>
          <button
            type="button"
            className="submit-button login-method-btn-wechat"
            onClick={onWeChat}
            disabled={configured === null || wechatBusy}
          >
            {wechatBusy ? '正在加载…' : '微信登录'}
          </button>
        </div>
        {wechatErr ? <p className="wechat-done-msg wechat-done-err" style={{ marginTop: 12 }}>{wechatErr}</p> : null}
      </div>
      {showQrModal ? (
        <div className="wechat-qr-modal-mask" role="presentation" onClick={closeQrModal}>
          <div className="wechat-qr-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="wechat-qr-modal-header">
              <h3>微信扫码登录</h3>
              <button type="button" className="wechat-qr-modal-close" onClick={closeQrModal}>
                关闭
              </button>
            </div>
            <WeChatOAuthQrDisplay authUrl={qrAuthUrl} loading={qrLoading} fetchError={qrFetchErr} />
            {qrPollingKey && !qrFetchErr ? (
              <p className="wechat-done-msg" style={{ marginTop: 12 }}>
                在手机微信上确认授权后，电脑将自动进入首页
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </AppShell>
  );
};

export default LoginMethod;
